import { Popover, IconButton, Typography, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { DateCalendar } from '@mui/x-date-pickers';
import { isSameDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { fMonthDay, parseDateOnlyToLocalDate } from '../../../utils/formatTime';
import { updateBudgetDate } from '../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../redux/store';
import Iconify from '../../../reusable-components/iconify';
import { AwaitButton } from '../../../reusable-components/await-button';

AdjustBudgetDate.propTypes = {
  budgetDate: PropTypes.any,
  orderId: PropTypes.string,
  iconOnly: PropTypes.bool,
};

export default function AdjustBudgetDate({ budgetDate, orderId, iconOnly }) {
  const dispatch = useDispatch();
  const { orderById } = useSelector((state) => state.orders);
  const canEditOrder = orderById[orderId]?.canEditOrder;

  const [selectedDate, setSelectedDate] = useState(null);
  const [isAdjusted, setIsAdjusted] = useState(false);
  const [changeBudgetDate, setChangeBudgetDate] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const baseDate = useMemo(() => parseDateOnlyToLocalDate(budgetDate), [budgetDate]);

  useEffect(() => {
    setSelectedDate(baseDate);
    setIsAdjusted(false);
  }, [baseDate]);

  const iconTooltip = useMemo(() => {
    if (!canEditOrder) return "Permission needed to adjust budget date";
    const current = selectedDate || baseDate;
    return current ? `Currently selected: ${fMonthDay(current)}` : "No budget date set";
  }, [selectedDate, baseDate, canEditOrder]);

  const displayLabel = useMemo(() => {
    if (iconOnly) return null;
    if (isAdjusted) return selectedDate instanceof Date ? `Budget date adjusted to: ${fMonthDay(selectedDate)}` : 'Budget date adjusted';
    if (baseDate && baseDate instanceof Date) return `Budget date: ${fMonthDay(baseDate)}`;
    return "Adjust budget date";
  }, [isAdjusted, selectedDate, baseDate, iconOnly]);

  const isBudgetDateUpdated = (date) => (!baseDate && date) || (baseDate && !date) || (baseDate && date && !isSameDay(date, baseDate));

  const saveBudget = async () => {
    if (!isBudgetDateUpdated(selectedDate) || !selectedDate) return;
    const response = await dispatch(updateBudgetDate(orderId, selectedDate));
    if (response) {
      setIsAdjusted(true);
      setChangeBudgetDate(false);
    }
  };

  const clickIcon = (e) => {
    setAnchorEl(e.currentTarget);
    setChangeBudgetDate(true);
    setSelectedDate(baseDate);
    setIsAdjusted(false);
  }

  return <>
    <Popover
      anchorEl={anchorEl}
      open={changeBudgetDate}
      onClose={() => {
        setAnchorEl(null);
        setChangeBudgetDate(false);
        setSelectedDate(baseDate);
        setIsAdjusted(false);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <DateCalendar
          value={selectedDate}
          onChange={(val) => {
            setSelectedDate(val);
            setIsAdjusted(isBudgetDateUpdated(val));
          }}
          sx={{ width: '100%', height: '100%' }}
        />

        <AwaitButton
          sx={{ mr: 1, mb: 1, position: 'absolute', bottom: 10, right: 10 }}
          color="secondary"
          size="small"
          variant="outlined"
          onClick={saveBudget}
          disabled={!isAdjusted}
        >
          Save
        </AwaitButton>
      </div>
    </Popover>

    {iconOnly ? <Tooltip arrow title={iconTooltip}>
      <span>
        <IconButton color="secondary" disableRipple disabled={!orderId || !canEditOrder} onClick={clickIcon} sx={{ ml: -1 }}>
          <Iconify icon="ic:sharp-build-circle" width={15} height={15} />
        </IconButton>
      </span>
    </Tooltip> :
      <Typography variant="body2" sx={{ py: 0.5, display: "flex", alignItems: "center" }}>
        <Tooltip arrow title={iconTooltip}>
          <Iconify sx={{ mr: 0.5 }} icon={"ic:sharp-build-circle"} width={16} height={16} color="secondary" onClick={(e) => {
            if (!orderId || !canEditOrder) return;
            clickIcon(e);
          }} />
        </Tooltip>
        {displayLabel}
      </Typography>}
  </>;
}